import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Check, X, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Podium, RehearsalBanner } from "@/components/games/Podium";

const KEY = (code) => `mya-quiz-${code}`;

function Join({ code, info, onJoined }) {
  const [f, setF] = useState({ table: "", name: "", captain: "" });
  const tables = info.join_mode === "tables";
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault();
    if (tables && !f.table) return toast.error("Choisissez votre table.");
    setBusy(true);
    try { const r = await api.post(`/games/live/${code}/join`, { ...f, table: tables ? Number(f.table) : null }); localStorage.setItem(KEY(code), JSON.stringify(r.data)); onJoined(r.data); }
    catch (er) { toast.error(formatApiError(er)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={go} className="space-y-4" data-testid="quiz-join-form">
      <p className="text-white/70">Inscrivez votre équipe pour jouer depuis ce téléphone.</p>
      {tables && <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Votre table</p>
        <div className="grid grid-cols-5 gap-2" data-testid="quiz-join-tables">
          {Array.from({ length: info.tables }, (_, i) => i + 1).map((n) => {
            const taken = info.taken.includes(n);
            return <button key={n} type="button" disabled={taken} onClick={() => setF({ ...f, table: n })} data-testid={`quiz-table-${n}`}
              className={`h-12 border text-sm ${f.table === n ? "border-[#DCB457] bg-[#DCB457] text-black" : taken ? "border-white/5 text-white/20 line-through" : "border-white/15"}`}>{n}</button>;
          })}
        </div>
      </div>}
      <input required maxLength={40} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Nom de l'équipe" className="input-luxe w-full min-h-[52px]" data-testid="quiz-join-name" />
      <input maxLength={40} value={f.captain} onChange={(e) => setF({ ...f, captain: e.target.value })} placeholder="Prénom du capitaine (facultatif)" className="input-luxe w-full min-h-[52px]" data-testid="quiz-join-captain" />
      <button disabled={busy} className="btn-gold w-full !py-4" data-testid="quiz-join-submit">{busy ? "Inscription…" : "Inscrire mon équipe"}</button>
    </form>
  );
}

function AnswerForm({ code, me, q, mine, onSent }) {
  const [a, setA] = useState(mine || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setA(mine || {}); }, [q.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async (ans) => {
    setBusy(true);
    try { await api.post(`/games/live/${code}/answer`, { team: me.team_id, token: me.token, question_id: q.id, answer: ans }); onSent(ans); toast.success("Réponse envoyée"); }
    catch (er) { toast.error(formatApiError(er)); } finally { setBusy(false); }
  };
  if (q.choices?.length) return (
    <div className="space-y-2" data-testid="quiz-choices">
      {q.choices.map((c, i) => (
        <button key={c} disabled={busy} onClick={() => send({ choice: c })} data-testid={`quiz-choice-${i}`}
          className={`w-full text-left p-4 border min-h-[56px] transition-colors ${mine?.choice === c ? "border-[#DCB457] bg-[#DCB457]/15 text-[#DCB457]" : "border-white/15 active:bg-white/5"}`}>
          <span className="text-[#DCB457] mr-3 font-serif-luxe">{String.fromCharCode(65 + i)}</span>{c}
        </button>
      ))}
    </div>
  );
  const fields = q.fields?.length ? q.fields : ["value"];
  return (
    <form onSubmit={(e) => { e.preventDefault(); send(a); }} className="space-y-2" data-testid="quiz-text-form">
      {fields.map((f) => <input key={f} value={a[f] || ""} onChange={(e) => setA({ ...a, [f]: e.target.value })} placeholder={f === "value" ? "Votre réponse" : f} className="input-luxe w-full min-h-[52px]" data-testid={`quiz-input-${f}`} />)}
      <button disabled={busy} className="btn-gold w-full !py-4" data-testid="quiz-send">{mine ? "Modifier ma réponse" : "Envoyer la réponse"}</button>
    </form>
  );
}

function Countdown({ seconds }) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => { const t = setInterval(() => setLeft((x) => Math.max(0, x - 1)), 1000); return () => clearInterval(t); }, []);
  return <span className={`font-serif-luxe text-3xl ${left <= 5 ? "text-red-300" : "text-[#DCB457]"}`} data-testid="quiz-countdown">{left}s</span>;
}

function Board({ rows, myId }) {
  return (
    <ol className="space-y-1.5" data-testid="quiz-board">
      {rows.map((b, i) => (
        <li key={b.id} className={`flex items-center gap-3 p-3 border ${b.id === myId ? "border-[#DCB457] bg-[#DCB457]/10" : "border-white/10"}`}>
          <span className="font-serif-luxe text-2xl w-8 text-[#DCB457]">{i + 1}</span><span className="flex-1">{b.table ? `Table ${b.table} · ` : ""}{b.name}</span><b>{b.score} pt{b.score > 1 ? "s" : ""}</b>
        </li>
      ))}
    </ol>
  );
}

export default function QuizPlay() {
  const { code } = useParams();
  const [info, setInfo] = useState(null);
  const [me, setMe] = useState(() => { try { return JSON.parse(localStorage.getItem(KEY(code)) || "null"); } catch { return null; } });
  const [st, setSt] = useState(null);
  const timer = useRef(null);
  const poll = useCallback(async () => {
    if (!me) return;
    try { setSt((await api.get(`/games/live/${code}/state`, { params: { team: me.team_id, token: me.token } })).data); }
    catch (e) { if (e?.response?.status === 401 || e?.response?.status === 404) { localStorage.removeItem(KEY(code)); setMe(null); } }
  }, [code, me]);
  useEffect(() => { api.get(`/games/live/${code}`).then((r) => setInfo(r.data)).catch((e) => setInfo({ error: formatApiError(e) })); }, [code, me]);
  useEffect(() => { poll(); timer.current = setInterval(poll, 2500); return () => clearInterval(timer.current); }, [poll]);
  if (!info) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#DCB457]" /></div>;
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="quiz-play">
    {info.rehearsal && <RehearsalBanner />}
    <div className="px-5 py-8 max-w-md mx-auto">
      <p className="text-[10px] uppercase tracking-[0.3em] text-[#DCB457]">My Animation · jeu en direct</p>
      <h1 className="font-serif-luxe text-3xl mt-2 mb-6" data-testid="quiz-title">{info.title || "Quiz"}</h1>
      {info.error ? <p className="text-red-300" data-testid="quiz-error">{info.error}</p>
        : !me ? <Join code={code} info={info} onJoined={setMe} />
        : !st ? <Loader2 className="animate-spin text-[#DCB457]" />
        : <Playing code={code} me={me} st={st} setSt={setSt} />}
    </div>
    </div>
  );
}

function Playing({ code, me, st, setSt }) {
  const t = st.team;
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between border border-white/10 p-3" data-testid="quiz-team">
        <span>{t.table ? `Table ${t.table} · ` : ""}<b>{t.name}</b>{t.captain && <span className="text-white/45 text-xs block">Capitaine : {t.captain}</span>}</span>
        <span className="text-right"><b className="gold-text text-xl" data-testid="quiz-score">{t.score}</b> pt{t.score > 1 ? "s" : ""}<span className="text-white/45 text-xs block">{t.rank === 1 ? "1er" : `${t.rank}e`}</span></span>
      </div>
      {st.status === "lobby" && <p className="text-white/70" data-testid="quiz-wait">Équipe inscrite ! Le jeu va bientôt commencer, gardez ce téléphone à portée de main.</p>}
      {st.status === "ready" && <p className="text-white/70" data-testid="quiz-next-wait">Préparez-vous : la prochaine question arrive…</p>}
      {st.question && (
        <div className="space-y-4" data-testid="quiz-question">
          <div className="flex justify-between items-center"><p className="text-xs text-white/45">Question {st.index + 1} / {st.total}</p>{st.remaining != null && <Countdown key={`${st.index}-${st.updated_at}`} seconds={st.remaining} />}</div>
          <p className="font-serif-luxe text-2xl leading-snug">{st.question.text}</p>
          {st.status === "question" && <AnswerForm code={code} me={me} q={st.question} mine={st.my_answer} onSent={(a) => setSt({ ...st, my_answer: a })} />}
          {st.status === "question" && st.my_answer && <p className="text-xs text-emerald-200" data-testid="quiz-sent">Réponse enregistrée — modifiable jusqu'à la fin du chrono.</p>}
          {st.status === "closed" && <p className="text-white/70 border border-white/10 p-4" data-testid="quiz-closed">Les réponses sont clôturées.{st.my_answer ? " Votre réponse est bien enregistrée." : " Aucune réponse envoyée pour cette question."} Suspense…</p>}
          {st.status === "reveal" && (
            <div className="border border-[#DCB457]/40 p-4 space-y-2" data-testid="quiz-reveal">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#DCB457]">La bonne réponse</p>
              <p className="text-lg">{st.star_answer || "non renseignée"}</p>
              {!st.awarded ? <p className="text-white/55" data-testid="quiz-result">L'animateur valide les réponses…</p>
                : <p className={st.correct ? "text-emerald-300" : "text-white/55"} data-testid="quiz-result">{st.correct ? <><Check size={16} className="inline" /> Bravo, +1 point !</> : st.my_answer ? <><X size={16} className="inline" /> Pas cette fois…</> : "Pas de réponse envoyée."}</p>}
            </div>
          )}
        </div>
      )}
      {st.status === "podium" && <Podium board={st.board} message={st.final_message} myId={t.id} testid="quiz-podium" />}
      {st.board && st.status !== "podium" && (
        <div className="space-y-3">
          <p className="font-serif-luxe text-2xl inline-flex items-center gap-2"><Trophy className="text-[#DCB457]" />{st.status === "finished" ? "Classement final" : "Classement provisoire"}</p>
          {st.status === "finished" && st.final_message && <p className="font-serif-luxe italic text-lg text-white/80" data-testid="quiz-final-message">{st.final_message}</p>}
          <Board rows={st.board} myId={t.id} />
        </div>
      )}
    </div>
  );
}
